import {
  getMeVisibilityGroupsTeams,
  getPlayersSearch,
  getTeamsTeamidSearch,
  OrgRoles,
  PlayerPublic,
  TeamDB,
  UserOrg,
} from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";

import { isMagnusAdminUser } from "@/lib/permissions";
import { removeOrgPrefix } from "@/lib/utils";
import { parseUploadedMembers } from "@/utils/uploadedPlayers";

import { LineUpPlayer } from "../types/gameFull";
import { TeamRoster } from "../types/roster";
import {
  getMemberExternalId,
  getMemberJerseyNumber,
  playerPublicToLineUpPlayer,
  RosterMember,
  uploadedPlayerToLineUpPlayer,
} from "./omnibusMappers";

// Omnibus returns parent_name, but its OpenAPI spec doesn't list it yet
type TeamDBWithParentName = TeamDB & { parent_name?: string };

/** A game team's omnibus team record, which roster writes need. */
export type TeamRecord = {
  /**
   * The org that owns the roster (`team.league`). Omnibus checks roster edits
   * against it, and players added to the roster are created in it.
   */
  orgId: string;
  /** Internal roster id for /rosters/{roster_id}/… writes */
  rosterId: string;
  team: TeamDBWithParentName;
};

/**
 * A game team's players, plus its team record when omnibus has one for its
 * external id. Games only carry the team's external id.
 */
export type TeamRosterData = {
  roster: TeamRoster;
  /** Null when the team isn't found: the players still load, but the roster can't be edited */
  record: TeamRecord | null;
};

export const TEAM_ROSTER_QUERY_KEY = "scorekeepingTeamRoster";

const teamRosterQueryKey = (
  teamExternalId: string | undefined,
  userOrgIds: string[],
) => [TEAM_ROSTER_QUERY_KEY, teamExternalId, userOrgIds];

/** Org roles omnibus lets edit an org's rosters (MANAGING_ORG_ROLES) */
const MANAGING_ORG_ROLES: OrgRoles[] = [
  "account_owner",
  "staff_admin",
  "staff",
];

/**
 * Whether the user can add players to a roster owned by `orgId`: omnibus
 * allows Magnus admins and anyone with a managing role in that org (a role in
 * another org the roster is shared with doesn't count).
 */
export const canEditRosterOrg = (
  orgId: string | undefined,
  userRoles: string[],
  userOrganizations: UserOrg[],
) => {
  if (isMagnusAdminUser(userRoles as Parameters<typeof isMagnusAdminUser>[0])) {
    return true;
  }
  if (!orgId) return false;
  const id = removeOrgPrefix(orgId);
  return userOrganizations.some(
    (org) =>
      removeOrgPrefix(org.orgId) === id &&
      org.orgRoles.some((role) => MANAGING_ORG_ROLES.includes(role)),
  );
};

/** Guards against a runaway `continue` loop on huge rosters. */
const MAX_PLAYER_PAGES = 10;

/**
 * The record to use when an external id matches several teams: a visible one
 * with a roster id, owned by one of the user's orgs if any is, else an
 * unofficial ("ORG#…") one, else the first.
 */
export const pickTeam = (
  teams: TeamDB[],
  userOrgIds: string[],
): TeamDB | undefined => {
  const candidates = teams.filter((team) => team.id && !team.hidden);
  const userOrgs = new Set(userOrgIds.map(removeOrgPrefix));
  return (
    candidates.find((team) => userOrgs.has(removeOrgPrefix(team.league))) ??
    candidates.find((team) => team.league.startsWith("ORG#")) ??
    candidates[0]
  );
};

/**
 * The team's record by its external id, whichever org owns it: the game only
 * carries the external id. Editing it is still gated on the owning org.
 */
export const findTeam = async (
  teamExternalId: string,
  userOrgIds: string[],
): Promise<TeamRecord | null> => {
  const { data } = await getTeamsTeamidSearch({
    query: { teamId: teamExternalId },
    throwOnError: true,
  });
  const teams = data?.teams ?? [];
  const team = pickTeam(teams, userOrgIds);
  if (teams.length > 1) {
    console.warn(
      `[rosters] ${teams.length} teams have external id ${teamExternalId}; using ${team?.id}`,
    );
  }
  return team?.id ? { team, rosterId: team.id, orgId: team.league } : null;
};

const fetchTeamPlayersInGroup = async (
  teamExternalId: string,
  visibilityGroup: string,
) => {
  const players: PlayerPublic[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < MAX_PLAYER_PAGES; page++) {
    const { data } = await getPlayersSearch({
      query: { visibilityGroup, teamId: teamExternalId, continue: cursor },
      throwOnError: true,
    });
    players.push(...(data?.players ?? []));
    cursor = data?.continue;
    if (!cursor) break;
  }

  return players;
};

/**
 * The team's players from every visibility group, without duplicates (like
 * the V1 tagger's useGetActiveGame): a team's players can be shared into
 * several groups.
 */
const fetchTeamPlayers = async (
  teamExternalId: string,
  visibilityGroups: string[],
) => {
  const players = (
    await Promise.all(
      visibilityGroups.map((visibilityGroup) =>
        fetchTeamPlayersInGroup(teamExternalId, visibilityGroup),
      ),
    )
  ).flat();
  return [...new Map(players.map((p) => [p.external_ids[0], p])).values()];
};

const fetchTeamRoster = async (
  teamExternalId: string,
  userOrgIds: string[],
): Promise<TeamRosterData> => {
  // The player search still needs groups: a team's players aren't all on its
  // record's members
  const fetchPlayers = async () => {
    const { data } = await getMeVisibilityGroupsTeams({ throwOnError: true });
    return fetchTeamPlayers(teamExternalId, data?.visibilityGroups ?? []);
  };

  // A failed team lookup only costs editing: the players still load
  const findTeamOrNull = async () => {
    try {
      return await findTeam(teamExternalId, userOrgIds);
    } catch (error) {
      console.warn(`[rosters] team lookup failed for ${teamExternalId}`, error);
      return null;
    }
  };

  const [players, record] = await Promise.all([
    fetchPlayers(),
    findTeamOrNull(),
  ]);

  return {
    record,
    roster: {
      rosterName: record?.team.known_names?.[0] ?? "",
      parentName: record?.team.parent_name || undefined,
      players: toRosterPlayers(players, record?.team.unofficial_members ?? []),
    },
  };
};

/**
 * A team's players: those the team search found, then the roster's members
 * it missed. The search matches a player's official team history, which the
 * members of an unofficial roster (added in Manage Rosters, Add Players or a
 * roster upload) don't have, so for most Magnus rosters the members are the
 * roster. Jersey numbers live on the members, not the player.
 */
export const toRosterPlayers = (
  searched: PlayerPublic[],
  members: RosterMember[],
): LineUpPlayer[] => {
  const jerseyNumbers = new Map(
    members.map((member) => [
      getMemberExternalId(member),
      getMemberJerseyNumber(member),
    ]),
  );
  const players = searched.map((player) =>
    playerPublicToLineUpPlayer(
      player,
      jerseyNumbers.get(player.external_ids[0]),
    ),
  );
  const found = new Set(players.map((p) => p.playerId));
  const missed = parseUploadedMembers(members)
    .filter((member) => !found.has(member.externalId))
    .map(uploadedPlayerToLineUpPlayer);
  return [...players, ...missed];
};

export const useTeamRoster = (
  teamExternalId: string | undefined,
  /** Breaks a tie between teams sharing the external id */
  userOrgIds: string[],
) =>
  useQuery({
    queryKey: teamRosterQueryKey(teamExternalId, userOrgIds),
    queryFn: () => fetchTeamRoster(teamExternalId!, userOrgIds),
    enabled: !!teamExternalId,
  });
